import TutorialService from "@/Service/TutorialService";

/**
 * Persistence for per-user tutorial history (who passed which tour).
 *
 * Server is the source of truth (`/api/v1/tutorials/state`), so completion
 * follows the person across browsers and devices. A localStorage copy, keyed by
 * uid, is kept purely as a cache: it lets the post-login decision run before the
 * request lands and keeps the page usable if the API is briefly unreachable.
 *
 * Shape (unchanged for callers):
 *   { byId: { [tutorialId]: { status: "COMPLETED"|"SKIPPED", versionSeen: number, updatedAt: ISO } },
 *     preferences: { suppressPrompts: boolean } }
 */

const KEY_PREFIX = "clipn:tutorial-state-v1:";
export const SESSION_PROMPT_KEY = "clipn:tutorial-prompt-shown-v1";

export const emptyTutorialState = () => ({
  byId: {},
  preferences: { suppressPrompts: false },
});

const storageKey = (uid) => `${KEY_PREFIX}${uid || "anonymous"}`;

const readCache = (uid) => {
  try {
    const raw = window.localStorage.getItem(storageKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      byId: parsed.byId && typeof parsed.byId === "object" ? parsed.byId : {},
      preferences: { suppressPrompts: !!parsed.preferences?.suppressPrompts },
    };
  } catch {
    return null;
  }
};

const writeCache = (uid, state) => {
  try {
    window.localStorage.setItem(storageKey(uid), JSON.stringify(state));
  } catch {
    /* private mode / quota — the server copy is still authoritative */
  }
};

/** Server response → the shape the slice works with. */
const fromServer = (data) => {
  const byId = {};
  for (const row of data?.tutorials || []) {
    if (!row?.tutorialId) continue;
    byId[row.tutorialId] = {
      status: row.status,
      versionSeen: Number(row.versionSeen) || 1,
      completedAt: row.completedAt || null,
      updatedAt: row.updatedAt || null,
    };
  }
  return { byId, preferences: { suppressPrompts: !!data?.suppressPrompts } };
};

export const loadTutorialState = async (uid) => {
  try {
    const state = fromServer(await TutorialService.getState());
    writeCache(uid, state);
    return state;
  } catch (error) {
    // Offline / API hiccup: fall back to what this browser last knew.
    console.warn("[tutorials] state fetch failed, using cached copy", error?.message);
    return readCache(uid) || emptyTutorialState();
  }
};

/**
 * Persist one tutorial's outcome. The caller passes the whole next state (it
 * already computed it optimistically); we send just the delta to the server and
 * cache the result.
 */
export const saveTutorialState = async (uid, state, change) => {
  writeCache(uid, state);
  try {
    if (change?.type === "tutorial") {
      await TutorialService.markTutorial(change.tutorialId, change.status, change.version);
    } else if (change?.type === "preferences") {
      await TutorialService.setPreferences(change.suppressPrompts);
    }
  } catch (error) {
    console.warn("[tutorials] state save failed, kept locally", error?.message);
  }
  return state;
};

/**
 * "The prompt already ran in this tab" marker.
 *
 * Scoped to the signed-in uid: signing out and into a second account in the
 * same tab must not inherit the first account's marker, which silently swallowed
 * the prompt for the second person. The literal "*" means "suppress for anyone"
 * and is what the e2e login helper seeds so upload specs never meet the dialog.
 */
export const wasPromptShownThisSession = (uid) => {
  try {
    const value = window.sessionStorage.getItem(SESSION_PROMPT_KEY);
    if (!value) return false;
    return value === "*" || (!!uid && value === uid);
  } catch {
    return false;
  }
};

export const rememberPromptShownThisSession = (uid) => {
  try {
    window.sessionStorage.setItem(SESSION_PROMPT_KEY, uid || "*");
  } catch {
    /* ignore */
  }
};

/**
 * Sign-out ends the "prompt session": without this, logging out and back in
 * inside the same tab kept the marker, so a re-login never re-offered a
 * pending tutorial ("at most once per tab session" was really "once per tab,
 * ever"). Called from the signOut thunk — NOT from component unmount, where
 * StrictMode's dev double-mount would wipe it on every page load.
 */
export const clearPromptShownThisSession = () => {
  try {
    window.sessionStorage.removeItem(SESSION_PROMPT_KEY);
  } catch {
    /* ignore */
  }
};
